// src/contexts/SupabaseContext.js
import {
  AuthError,
  AuthTokenResponsePassword,
  createClient,
  Session,
  SupabaseClient,
  User,
} from "@supabase/supabase-js";
import { Database } from "../../database.types";
import { createContext, useContext, useState, useEffect } from "react";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  "https://nzktobdqsrzqfofdaqff.supabase.co";
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im56a3RvYmRxc3J6cWZvZmRhcWZmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjMxMjAyNDQsImV4cCI6MjA3ODY5NjI0NH0.ZK4pGV2lZK4ab9VsWB_dZo9Xb0fBVfVGCRy0snaZqTE";
const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

interface SupabaseProviderReturn {
  supabase: SupabaseClient;
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string
  ) => Promise<{ data: { user: User | null; session: Session | null }; error: null }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<AuthTokenResponsePassword>;
  signOut: () => Promise<{
    error: AuthError | null;
  }>;
  resetPassword: (email: string) => Promise<
    | {
        data: {};
        error: null;
      }
    | {
        data: null;
        error: AuthError;
      }
  >;
  isConfirmEmail: boolean;
  setIsConfirmEmail: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create context
const SupabaseContext = createContext<SupabaseProviderReturn | null>(null);

// Context provider component
export const SupabaseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [isConfirmEmail, setIsConfirmEmail] = useState<boolean>(false);

  useEffect(() => {
    // Get initial session
    const initSession = async () => {
      setLoading(true);

      // Check for existing session
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      updateSession(currentSession);

      // Set up auth state change listener
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, newSession) => {
        updateSession(newSession);
        supabase.realtime.setAuth(newSession?.access_token ?? null);
      });

      setLoading(false);

      // Clean up subscription on unmount
      return () => subscription.unsubscribe();
    };

    initSession();
  }, []);

  const updateSession = (newSession: Session | null) => {
    setSession(newSession);
    setUser(newSession?.user || null);
  };

  // Authentication functions
  // trigger on users db should handle the creation of the league row in the db
  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    // create users table entry
    setIsConfirmEmail(true);
    return { data, error };
  };

  const signIn = async (email: string, password: string) =>
    await supabase.auth.signInWithPassword({ email, password });

  const signOut = () => supabase.auth.signOut();

  const resetPassword = (email: string) =>
    supabase.auth.resetPasswordForEmail(email);

  // Create value object
  const value = {
    // Supabase instance
    supabase,
    // Auth state
    session,
    user,
    loading,
    // Auth methods
    signUp,
    signIn,
    signOut,
    resetPassword,
    isConfirmEmail,
    setIsConfirmEmail,
  };

  // Return provider with value
  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

// Custom hook for easy context use
export function useSupabase() {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }
  return context;
}
